<template>
  <div class="root">
    <ZoomPhoneConnectionError />
    <div
      v-if="bannerStates.size > 0"
      class="banner-container"
    >
      <VoiceAnalysisBanner
        v-for="[id, task] in bannerStates"
        :key="id"
        :target-task="task"
        @dismiss="() => dismissBanner(id)"
      />
    </div>
    <div>
      <CallListFilter
        v-model:started-at-from="startedAtFrom"
        v-model:started-at-to="startedAtTo"
        v-model:caller-user-id="callerUserId"
        v-model:callee-did-number="calleeDidNumber"
      />
      <CallListPageControl
        v-model:current-page="currentPage"
        :meta="meta"
      >
        <template #actions>
          <BBtn
            class="call-list-import-btn"
            type="secondary"
            size="small"
            @click="openImportModal"
          >
            <BIcon>add</BIcon>
            {{ $t('vaApp.callImport.button') }}
          </BBtn>
        </template>
      </CallListPageControl>
    </div>
    <div>
      <CallListTable
        :is-loading="isLoading"
        :zoom-phone-results="zoomPhoneResults"
        :analyzing-ids="analyzingIds"
        @analyze="handleAnalyze"
      />
    </div>
    <ManualCallImportModal
      modal-name="manual-call-import-modal"
      :is-loading="isImportLoading"
      @submit="handleImportSubmit"
      @close="closeModal"
    />
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce';
import { ref, computed, watch, onMounted, onUnmounted, inject, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';
import { PaginationMeta, ZoomPhoneResult, VoiceAnalysisStatus as VoiceAnalysisStatusEnum } from '@/api/openapi';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import CallListFilter from '@/components/organisms/va-app/callList/CallListFilter.vue';
import CallListPageControl from '@/components/organisms/va-app/callList/CallListPageControl.vue';
import CallListTable from '@/components/organisms/va-app/callList/callListTable/CallListTable.vue';
import ManualCallImportModal from '@/components/organisms/va-app/callList/ManualCallImportModal.vue';
import VoiceAnalysisBanner from '@/components/organisms/va-app/callList/VoiceAnalysisBanner.vue';
import ZoomPhoneConnectionError from '@/components/organisms/va-app/callList/ZoomPhoneConnectionError.vue';
import { useQueryParameter } from '@/composable/query-parameter';
import { useToast } from '@/composable/toast';
import { useManualCallImport } from '@/composable/va-app/manual-call-import';
import { useVoiceAnalysisManager } from '@/composable/va-app/voice-analysis/voice-analysis-manager';
import { REFRESH_CALL_LIST_KEY, TRACK_VOICE_ANALYSIS_KEY } from '@/composable/va-app/zoom-phone-widget/injection-keys';
import { formatISO8601InMinute } from '@/utils/date-time';

const instance = getCurrentInstance();

const zoomPhoneResults = ref<ZoomPhoneResult[]>([]);
const meta = ref<PaginationMeta>({
  totalPages: 0,
  totalCount: 0,
  limitValue: 0,
  currentPage: 1,
  isEmpty: false,
});

const { bannerStates, analyzingIds, startAnalysis, resumePollingForResults, dismissBanner, trackAnalysis } = useVoiceAnalysisManager({
  onStatusUpdate: (id: number, status: VoiceAnalysisStatusEnum) => {
    const result = zoomPhoneResults.value.find(r => r.id === id);
    if (result) {
      result.voiceAnalysisStatus = status;
    }
  },
});

// ページやフィルタを変更した際に全件ステータスAPIが発火するのを防ぐためデバウンス
const debouncedResumePolling = debounce(() => {
  void resumePollingForResults();
}, 500);

const pageParamValidator = (query: string | string[]) => {
  const value = Number(query);
  return Number.isFinite(value) && value >= 1 ? value : 1;
};

const { param: currentPageParam } = useQueryParameter<number>('page', pageParamValidator);
const { param: startedAtFromParam } = useQueryParameter<string>('startedAtFrom');
const { param: startedAtToParam } = useQueryParameter<string>('startedAtTo');
const { param: callerUserIdParam } = useQueryParameter<string>('callerUserId');
const { param: calleeDidNumberParam } = useQueryParameter<string>('calleeDidNumber');

const currentPage = computed({
  get: () => currentPageParam.value,
  set: (value: number) => {
    currentPageParam.value = value;
  },
});
const startedAtFrom = computed({
  get: () => startedAtFromParam.value,
  set: (value: string | undefined) => {
    startedAtFromParam.value = value || undefined;
    currentPage.value = 1;
  },
});
const startedAtTo = computed({
  get: () => startedAtToParam.value,
  set: (value: string | undefined) => {
    startedAtToParam.value = value || undefined;
    currentPage.value = 1;
  },
});
const callerUserId = computed({
  get: () => callerUserIdParam.value,
  set: (value: string | undefined) => {
    callerUserIdParam.value = value || undefined;
    currentPage.value = 1;
  },
});
const calleeDidNumber = computed({
  get: () => calleeDidNumberParam.value,
  set: (value: string | undefined) => {
    calleeDidNumberParam.value = value || undefined;
    currentPage.value = 1;
  },
});

const isLoading = ref(false);
const { errorToast } = useToast();
const { t } = useI18n();

// NOTE:Zoom Phone Widget からコール一覧をリフレッシュするために inject
const refreshCallListRef = inject(REFRESH_CALL_LIST_KEY);
if (!refreshCallListRef) {
  throw new Error('CallList: REFRESH_CALL_LIST_KEY is not provided');
}

// NOTE:Zoom Phone Widget から音声分析を追跡するために inject
const trackVoiceAnalysisRef = inject(TRACK_VOICE_ANALYSIS_KEY);
if (!trackVoiceAnalysisRef) {
  throw new Error('CallList: TRACK_VOICE_ANALYSIS_KEY is not provided');
}

onMounted(() => {
  refreshCallListRef.value = fetchZoomPhoneResults;
  trackVoiceAnalysisRef.value = trackAnalysis;
});

onUnmounted(() => {
  debouncedResumePolling.cancel();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refreshCallListRef.value = async () => {
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trackVoiceAnalysisRef.value = () => {
  };
});

onBeforeRouteLeave((_to, _from) => {
  if (isImportLoading.value) {
    const confirmed = confirm(t('vaApp.callImport.routeLeaveConfirm'));
    if (!confirmed) {
      return false;
    }
  }
});

const fetchZoomPhoneResults = async () => {
  isLoading.value = true;
  try {
    const api = new ZoomPhoneResultApiService();
    const { data } = await api.getZoomPhoneResults({
      request: {
        page: currentPage.value,
        startedAtFrom: startedAtFrom.value === 'all' ? undefined : formatISO8601InMinute(startedAtFrom.value),
        startedAtTo: startedAtTo.value === 'all' ? undefined : formatISO8601InMinute(startedAtTo.value),
        callerUserId: callerUserId.value === 'all' ? undefined : callerUserId.value,
        calleeDidNumber: calleeDidNumber.value,
      },
    });
    const totalPages = data.meta.totalPages ?? 0;
    zoomPhoneResults.value = data.zoomPhoneResults;
    if (totalPages > 0 && data.meta.currentPage > totalPages) {
      currentPage.value = totalPages;
      return;
    }
    meta.value = data.meta;

    debouncedResumePolling();
  } finally {
    isLoading.value = false;
  }
};

const handleAnalyze = (id: number) => {
  const result = zoomPhoneResults.value.find(r => r.id === id);
  if (result) {
    startAnalysis(id, result.calleeDidNumber || undefined);
  }
};

const {
  openModal: openImportModal,
  closeModal,
  handleSubmit: handleImportSubmit,
  isLoading: isImportLoading,
  errorMessage: importErrorMessage,
} = useManualCallImport({
  onImportSuccess: async (resultId, phoneNumber) => {
    if (instance?.isUnmounted) return;
    startAnalysis(resultId, phoneNumber);
    await fetchZoomPhoneResults();
  },
});

// エラーメッセージを監視してトースト表示（表示内容の判定は composable の errorHandlers で実施）
watch(importErrorMessage, (msg) => {
  if (msg) {
    const translated = t(`apiError.message.${msg}`);
    const isResolvedKey = translated !== `apiError.message.${msg}`;
    errorToast(isResolvedKey ? translated : msg);
  }
});

watch([
  currentPage,
  startedAtFrom,
  startedAtTo,
  callerUserId,
  calleeDidNumber,
], async () => {
  await fetchZoomPhoneResults();
}, { immediate: true });
</script>

<style lang="scss" scoped>
.root {
  margin-top: $header-height;
  width: 100%;
}

.banner-container {
  margin-top: $basespace-200;
}

.call-list-import-btn {
  margin : 0px $basespace-200;
  &:hover {
    background-color: $bgcolor-dark;
  }
}
</style>
